import type { LeitourDatabase } from '../database/db';
import { databaseManager } from '../database/databaseManager';
import type { CoverAsset } from '../domain/entities/types';
import { supabaseConfiguration } from '../config/supabase';
import type { SyncCoverConflict, SyncCoverState, SyncRemoteCoverMetadata } from './types';
import { syncExclusiveLock } from './coordination';

export type RemoteCoverMetadata = SyncRemoteCoverMetadata;

export interface CoverTransport {
  list(): Promise<RemoteCoverMetadata[]>;
  upload(path: string, blob: Blob, mimeType: string): Promise<void>;
  download(path: string): Promise<Blob>;
  saveMetadata(metadata: RemoteCoverMetadata): Promise<void>;
  tombstone(coverId: string): Promise<string | undefined>;
  remove(path: string): Promise<void>;
}

const extensionFor = (mimeType: string): string => {
  switch (mimeType) {
    case 'image/webp': return 'webp';
    case 'image/png': return 'png';
    case 'image/jpeg': return 'jpg';
    case 'image/gif': return 'gif';
    default: throw new Error('Formato de capa não permitido para sincronização.');
  }
};

const blobBytes = (blob: Blob): Promise<ArrayBuffer> => {
  if (typeof blob.arrayBuffer === 'function') return blob.arrayBuffer();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error ?? new Error('Não foi possível ler a capa local.'));
    reader.readAsArrayBuffer(blob);
  });
};

export const hashCoverBlob = async (blob: Blob): Promise<string> => {
  const digest = await crypto.subtle.digest('SHA-256', await blobBytes(blob));
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
};

export const pendingCoverState = async (
  userId: string,
  bookId: string,
  asset: CoverAsset
): Promise<SyncCoverState> => {
  const sha256 = await hashCoverBlob(asset.data);
  return {
    coverId: asset.id, bookId, sha256,
    storagePath: `${userId}/${asset.id}/${sha256}.${extensionFor(asset.mimeType)}`,
    mimeType: asset.mimeType, byteSize: asset.byteSize,
    status: 'pending_upload', updatedAt: new Date().toISOString()
  };
};

interface CoverRemoteClient {
  rpc(name: string, args?: Record<string, unknown>): PromiseLike<{
    data: unknown;
    error: { message: string; status?: number } | null;
  }>;
  storage: {
    from(bucket: string): {
      upload(path: string, blob: Blob, options: { contentType: string; upsert: boolean }): PromiseLike<{
        error: { message: string; statusCode?: string } | null;
      }>;
      download(path: string): PromiseLike<{ data: Blob | null; error: { message: string } | null }>;
      remove(paths: string[]): PromiseLike<{ error: { message: string } | null }>;
    };
  };
}

const requireRemote = (error: { message: string } | null): void => {
  if (error) throw new Error('Não foi possível sincronizar a capa.');
};

export const createSupabaseCoverTransport = (client: CoverRemoteClient): CoverTransport => ({
  async list() {
    const { data, error } = await client.rpc('list_cover_metadata');
    requireRemote(error);
    if (!Array.isArray(data)) throw new Error('Metadados remotos de capa inválidos.');
    return data as RemoteCoverMetadata[];
  },
  async upload(path, blob, mimeType) {
    const { error } = await client.storage.from('leitour-covers').upload(path, blob, {
      contentType: mimeType, upsert: false
    });
    if (error && error.statusCode !== '409' && !error.message.toLowerCase().includes('already exists')) {
      throw new Error('Não foi possível enviar a capa.');
    }
  },
  async download(path) {
    const { data, error } = await client.storage.from('leitour-covers').download(path);
    requireRemote(error);
    if (!data) throw new Error('A capa remota não foi encontrada.');
    return data;
  },
  async saveMetadata(metadata) {
    const { error } = await client.rpc('upsert_cover_metadata', { metadata });
    requireRemote(error);
  },
  async tombstone(coverId) {
    const { data, error } = await client.rpc('tombstone_cover_metadata', { target_cover_id: coverId });
    requireRemote(error);
    return typeof data === 'string' ? data : undefined;
  },
  async remove(path) {
    const { error } = await client.storage.from('leitour-covers').remove([path]);
    requireRemote(error);
  }
});

const remoteAsset = async (transport: CoverTransport, metadata: RemoteCoverMetadata): Promise<CoverAsset> => {
  const blob = await transport.download(metadata.storagePath);
  const sha256 = await hashCoverBlob(blob);
  if (sha256 !== metadata.sha256 || blob.size !== metadata.byteSize) {
    throw new Error('A capa remota falhou na verificação SHA-256.');
  }
  return {
    id: metadata.coverId, data: blob, mimeType: metadata.mimeType,
    width: metadata.width, height: metadata.height, byteSize: metadata.byteSize,
    createdAt: metadata.createdAt, updatedAt: metadata.updatedAt
  };
};

export class CoverSyncService {
  constructor(private readonly transport: CoverTransport) {}

  listOpenConflicts(db: LeitourDatabase): Promise<SyncCoverConflict[]> {
    return db.syncCoverConflicts.where('status').equals('open').sortBy('detectedAt');
  }

  private async applyRemote(db: LeitourDatabase, metadata: RemoteCoverMetadata): Promise<void> {
    if (metadata.deletedAt) {
      await db.transaction('rw', [db.books, db.syncCoverStates], async () => {
        const book = await db.books.get(metadata.bookId);
        if (book?.coverAssetId === metadata.coverId) {
          const withoutCover = { ...book };
          delete withoutCover.coverAssetId;
          await db.books.put(withoutCover);
        }
        const state = await db.syncCoverStates.get(metadata.coverId);
        if (state) await db.syncCoverStates.put({ ...state, remoteDeletedAt: metadata.deletedAt, updatedAt: new Date().toISOString() });
      });
      return;
    }
    const asset = await remoteAsset(this.transport, metadata);
    await db.transaction('rw', [db.books, db.coverAssets, db.syncCoverStates], async () => {
      await db.coverAssets.put(asset);
      await db.books.update(metadata.bookId, { coverAssetId: asset.id });
      await db.syncCoverStates.put({
        coverId: asset.id, bookId: metadata.bookId, sha256: metadata.sha256,
        storagePath: metadata.storagePath, mimeType: asset.mimeType, byteSize: asset.byteSize,
        status: 'cached', updatedAt: new Date().toISOString()
      });
    });
  }

  private async recordConflict(
    db: LeitourDatabase,
    bookId: string,
    localCoverId: string | undefined,
    remote: RemoteCoverMetadata
  ): Promise<void> {
    const existing = await db.syncCoverConflicts.where('bookId').equals(bookId)
      .filter(({ status }) => status === 'open').first();
    if (existing) return;
    await db.syncCoverConflicts.add({
      id: crypto.randomUUID(), bookId, status: 'open', reason: 'replacement_conflict',
      ...(localCoverId ? { localCoverId } : {}),
      remoteCoverId: remote.coverId, remoteMetadata: remote,
      detectedAt: new Date().toISOString()
    });
  }

  private async cleanOrphan(db: LeitourDatabase, state: SyncCoverState): Promise<string> {
    let deletedAt = state.remoteDeletedAt;
    let path = state.storagePath;
    if (!deletedAt) {
      path = await this.transport.tombstone(state.coverId) ?? path;
      deletedAt = new Date().toISOString();
      await db.syncCoverStates.put({ ...state, storagePath: path, remoteDeletedAt: deletedAt, updatedAt: deletedAt });
    }
    await this.transport.remove(path);
    await db.syncCoverStates.delete(state.coverId);
    return deletedAt;
  }

  private async coreBookReady(db: LeitourDatabase, bookId: string, localBookExists: boolean): Promise<boolean> {
    const activeCoreBatch = await db.syncOutbox.filter(({ status, batch }) =>
      status !== 'superseded'
      && batch.mutations.some(({ entityType, entityId }) => entityType === 'book' && entityId === bookId)).first();
    if (activeCoreBatch) return false;
    const openConflict = await db.syncConflicts.where('[entityType+entityId]').equals(['book', bookId])
      .filter(({ status }) => status === 'open').first();
    if (openConflict) return false;
    const state = await db.syncEntityStates.get(['book', bookId]);
    if (!state) return false;
    return localBookExists ? !state.deletedAt : Boolean(state.deletedAt);
  }

  async reconcile(db: LeitourDatabase, userId: string): Promise<void> {
    const remote = await this.transport.list();
    for (const state of await db.syncCoverStates.where('status').equals('orphaned').toArray()) {
      const localBookExists = Boolean(await db.books.get(state.bookId));
      if (!await this.coreBookReady(db, state.bookId, localBookExists)) continue;
      const deletedAt = await this.cleanOrphan(db, state);
      const index = remote.findIndex(({ coverId }) => coverId === state.coverId);
      if (index >= 0) remote[index] = { ...remote[index]!, deletedAt };
    }

    for (const book of await db.books.toArray()) {
      if (!await this.coreBookReady(db, book.id, true)) continue;
      const activeRemote = remote
        .filter(({ bookId, deletedAt }) => bookId === book.id && !deletedAt)
        .sort((left, right) => right.updatedAt.localeCompare(left.updatedAt))[0];
      const localCover = book.coverAssetId ? await db.coverAssets.get(book.coverAssetId) : undefined;
      const localState = localCover ? await db.syncCoverStates.get(localCover.id) : undefined;
      const localTombstone = localCover
        ? remote.find(({ coverId, deletedAt }) => coverId === localCover.id && Boolean(deletedAt))
        : undefined;

      if (localTombstone) {
        if (activeRemote && activeRemote.coverId !== localCover?.id) await this.applyRemote(db, activeRemote);
        else await this.applyRemote(db, localTombstone);
        continue;
      }
      if (activeRemote && localCover && activeRemote.coverId !== localCover.id) {
        await this.recordConflict(db, book.id, localCover.id, activeRemote);
        continue;
      }
      if (activeRemote && !localCover) {
        await this.applyRemote(db, activeRemote);
        continue;
      }
      if (!localCover) continue;

      const sha256 = await hashCoverBlob(localCover.data);
      if (activeRemote?.coverId === localCover.id) {
        if (activeRemote.sha256 !== sha256) {
          await this.recordConflict(db, book.id, localCover.id, activeRemote);
        } else if (!localState || localState.status === 'pending_upload') {
          await db.syncCoverStates.put({
            ...(localState ?? await pendingCoverState(userId, book.id, localCover)),
            status: 'uploaded', updatedAt: new Date().toISOString()
          });
        }
        continue;
      }
      if (localState?.status === 'uploaded' || localState?.status === 'cached') {
        // Ausência remota nunca é inferida como delete.
        continue;
      }
      const state = localState ?? await pendingCoverState(userId, book.id, localCover);
      await this.transport.upload(state.storagePath, localCover.data, localCover.mimeType);
      const metadata: RemoteCoverMetadata = {
        coverId: localCover.id, bookId: book.id, sha256, storagePath: state.storagePath,
        mimeType: localCover.mimeType, byteSize: localCover.byteSize,
        width: localCover.width, height: localCover.height,
        createdAt: localCover.createdAt, updatedAt: localCover.updatedAt
      };
      await this.transport.saveMetadata(metadata);
      await db.syncCoverStates.put({ ...state, sha256, status: 'uploaded', updatedAt: new Date().toISOString() });
    }
  }

  async acceptRemote(db: LeitourDatabase, id: string): Promise<void> {
    const conflict = await db.syncCoverConflicts.get(id);
    if (!conflict || conflict.status !== 'open' || !conflict.remoteMetadata) {
      throw new Error('Este conflito de capa já foi resolvido ou não possui snapshot remoto.');
    }
    const asset = conflict.remoteMetadata.deletedAt
      ? undefined
      : await remoteAsset(this.transport, conflict.remoteMetadata);
    await db.transaction('rw', [db.books, db.coverAssets, db.syncCoverStates, db.syncCoverConflicts], async () => {
      const book = await db.books.get(conflict.bookId);
      if (!book) throw new Error('O livro deste conflito não existe mais.');
      if (conflict.localCoverId && conflict.localCoverId !== conflict.remoteCoverId) {
        const state = await db.syncCoverStates.get(conflict.localCoverId);
        if (state?.status === 'uploaded' || state?.status === 'cached') {
          await db.syncCoverStates.put({ ...state, status: 'orphaned', updatedAt: new Date().toISOString() });
        } else if (state) await db.syncCoverStates.delete(state.coverId);
      }
      if (asset) {
        await db.coverAssets.put(asset);
        await db.books.update(book.id, { coverAssetId: asset.id });
        await db.syncCoverStates.put({
          coverId: asset.id, bookId: book.id, sha256: conflict.remoteMetadata!.sha256,
          storagePath: conflict.remoteMetadata!.storagePath, mimeType: asset.mimeType,
          byteSize: asset.byteSize, status: 'cached', updatedAt: new Date().toISOString()
        });
      } else {
        const withoutCover = { ...book };
        delete withoutCover.coverAssetId;
        await db.books.put(withoutCover);
      }
      await db.syncCoverConflicts.update(id, {
        status: 'resolved', resolution: 'accept_remote', resolvedAt: new Date().toISOString()
      });
    });
  }

  async keepLocal(db: LeitourDatabase, userId: string, id: string): Promise<void> {
    const conflict = await db.syncCoverConflicts.get(id);
    if (!conflict || conflict.status !== 'open' || !conflict.localCoverId) {
      throw new Error('Este conflito de capa já foi resolvido ou não possui capa local.');
    }
    const cover = await db.coverAssets.get(conflict.localCoverId);
    if (!cover) throw new Error('A capa local deste conflito não está mais disponível.');
    if (conflict.remoteCoverId) {
      const path = await this.transport.tombstone(conflict.remoteCoverId);
      if (path) await this.transport.remove(path);
    }
    const state = await pendingCoverState(userId, conflict.bookId, cover);
    await db.transaction('rw', [db.syncCoverStates, db.syncCoverConflicts], async () => {
      await db.syncCoverStates.put(state);
      await db.syncCoverConflicts.update(id, {
        status: 'resolved', resolution: 'keep_local', resolvedAt: new Date().toISOString()
      });
    });
  }

  async deleteOrphanAfterConfirmation(db: LeitourDatabase, coverId: string): Promise<void> {
    const state = await db.syncCoverStates.get(coverId);
    if (!state || state.status !== 'orphaned') throw new Error('A capa não foi confirmada como órfã.');
    const referenced = await db.books.filter(({ coverAssetId }) => coverAssetId === coverId).count();
    if (referenced) throw new Error('A capa ainda está associada a um livro.');
    await this.cleanOrphan(db, state);
    // O Blob local é deliberadamente preservado. Limpeza local é uma ação de
    // produto separada e nunca é inferida da ausência remota.
  }
}

export const coverSyncService = supabaseConfiguration.client
  ? new CoverSyncService(createSupabaseCoverTransport(
      supabaseConfiguration.client as unknown as CoverRemoteClient
    ))
  : null;

export const resolveActiveCoverConflict = async (
  service: CoverSyncService,
  id: string,
  action: 'remote' | 'local'
): Promise<SyncCoverConflict[]> => databaseManager.runWithActiveDatabase(async (db) => {
  const userId = databaseManager.getSnapshot().userId;
  if (!userId) throw new Error('Sessão autenticada necessária.');
  return syncExclusiveLock.run(userId, async () => {
    if (action === 'remote') await service.acceptRemote(db, id);
    else await service.keepLocal(db, userId, id);
    return service.listOpenConflicts(db);
  });
});
